import { Stack } from '@mui/joy';
import { BodyScrollEvent, GetDataPath, RowClassParams, RowHeightParams } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AgGridTable, SlidingPanelContent } from 'core/components';
import { Loader } from 'core/elements';
import { AgGridClientTableProvider } from 'core/states';
import { isPromotedSingleChild } from 'core/utils';
import { CorporatePartNotSetUp } from 'corporate/components/CorporatePartNotSetUp';
import { CorporatePartUpsertButton } from 'corporate/components/CorporatePartSetUp';
import { UpsertCorporatePartPriceButton } from 'corporate/components/UpsertCorporatePartPrice';
import { CorporatePartCostStatus } from 'corporate/enums';
import { CorporatePartHelper } from 'corporate/helpers';
import { useCanManageParts, useGetCorporatePartPrices } from 'corporate/hooks';
import { ICorporatePart, ICorporatePartPriceListPayload } from 'corporate/types';
import { EditPricingTypeButton, VendorPriceCard } from 'parts/components/PartDetails';
import { PricingType } from 'pricing/enums';
import { Direction } from 'theme/enums';

import { columnDef } from './columns';
import { flattenPriceRecords, IFlatPriceRow } from './utils';

interface IProps {
  corporatePart: Partial<ICorporatePart>;
  partNumber: string;
  manufacturerId: number;
  enabled?: boolean;
  fullWidth?: boolean;
  isLoading?: boolean;
  onBodyScroll?: (event: BodyScrollEvent) => void;
}

const LEAF_ROW_HEIGHT = 42;

const autoGroupColumnDef = {
  headerName: '',
  width: 50,
  maxWidth: 50,
  pinned: 'left' as const,
  lockPinned: true,
  suppressMovable: true,
  suppressHeaderMenuButton: true,
  suppressColumnsToolPanel: true,
  resizable: false,
  sortable: false,
  filter: false,
  cellRendererParams: {
    suppressCount: true,
    innerRenderer: () => null,
  },
};

export const CorporatePartPriceList = ({
  corporatePart,
  partNumber,
  manufacturerId,
  enabled = false,
  fullWidth = true,
  isLoading = false,
  onBodyScroll,
}: IProps) => {
  const { t } = useTranslation();

  const isSetUp = CorporatePartHelper.isSetUp(corporatePart);
  const canManageParts = useCanManageParts();
  const canManagePartPrices = canManageParts && isSetUp;
  const quantityUom = corporatePart?.uom;
  const priceUom = corporatePart?.originalListPriceUom || corporatePart?.pricingUom;
  const isManual = corporatePart?.pricingType === PricingType.MANUAL;
  const listPriceValue = isManual ? corporatePart?.manualListPrice : corporatePart?.listPrice;
  const floorPriceValue = isManual ? corporatePart?.manualFloorPrice : corporatePart?.floorPrice;

  const payload: ICorporatePartPriceListPayload = {
    filters: {
      corporatePartId: corporatePart?.id,
    },
    pageSize: 100,
  };

  const {
    records,
    isLoading: isDataLoading,
    loadingPercentage,
    currentPage,
  } = useGetCorporatePartPrices(payload, { enabled: enabled && isSetUp, fetchAllPages: true });

  const flatRows = useMemo(() => flattenPriceRecords(records, quantityUom), [records, quantityUom]);

  const defaultColDef = useMemo(() => ({ autoHeight: false }), []);

  const columnDefs = useMemo(
    () => columnDef({ corporatePart, canManagePartPrices }),
    [corporatePart, canManagePartPrices]
  );

  const getDataPath = useCallback<GetDataPath<IFlatPriceRow>>(
    (data) => [String(data.recordId), String(data.itemId ?? data.recordId)],
    []
  );

  const getRowClass = useCallback(({ data, node }: RowClassParams<IFlatPriceRow>) => {
    const status = node?.group ? node.allLeafChildren?.[0]?.data?.status : data?.status;

    const classes: string[] = [];

    if (status === CorporatePartCostStatus.EXPIRED || status === CorporatePartCostStatus.OLD) {
      classes.push('disabledRow');
    }

    // Resolve sibling position from the post-sort list so alternation tracks the
    // displayed order, not the source-data order.
    const groupNode = node?.group ? node : node?.parent;

    if (groupNode?.parent) {
      const siblings =
        groupNode.parent.childrenAfterSort ??
        groupNode.parent.childrenAfterFilter ??
        groupNode.parent.childrenAfterGroup;
      const index = siblings ? siblings.indexOf(groupNode) : -1;

      if (index >= 0 && index % 2 === 1) {
        classes.push('oddGroup');
      }
    }

    return classes.length ? classes : undefined;
  }, []);

  const getRowHeight = useCallback((params: RowHeightParams<IFlatPriceRow>) => {
    if (params.node?.group) {
      return undefined;
    }
    if (isPromotedSingleChild({ node: params.node })) {
      return undefined;
    }

    return LEAF_ROW_HEIGHT;
  }, []);

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: t('labelColumns'),
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
          },
        },
        {
          id: 'filters',
          labelDefault: t('labelFilters'),
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
        },
      ],
      defaultToolPanel: '',
    }),
    [t]
  );

  const slidingPanelExtra = useMemo(() => {
    if (!isSetUp) {
      return (
        <CorporatePartUpsertButton
          corporatePart={corporatePart}
          partNumber={partNumber}
          manufacturerId={manufacturerId}
        />
      );
    }

    return <UpsertCorporatePartPriceButton corporatePart={corporatePart} />;
  }, [isSetUp, corporatePart, partNumber, manufacturerId]);

  return (
    <AgGridClientTableProvider
      tableId="corporatePartPriceList"
      entityType="PriceItem"
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      data={flatRows}
      isDataLoading={isDataLoading}
      loadingPercentage={loadingPercentage}
      dataCurrentPage={currentPage}
      hasRowSelection={false}
      hasGroupByEnabled={false}
      treeData
      getDataPath={getDataPath}
      groupDefaultExpanded={-1}
      groupRemoveSingleChildren
      autoGroupColumnDef={autoGroupColumnDef}
      getRowClass={getRowClass}
      getRowHeight={getRowHeight}
      sideBar={sideBar}
      onBodyScroll={onBodyScroll}
    >
      <SlidingPanelContent
        title={t('titlePrices')}
        extra={!isLoading && slidingPanelExtra}
        fullWidth={fullWidth}
      >
        {isLoading && <Loader />}
        {!isLoading && isSetUp && (
          <Stack direction={Direction.ROW} sx={{ flexWrap: 'wrap', gap: 2 }}>
            <VendorPriceCard
              groupName={t('labelListPrice')}
              listPrice={listPriceValue}
              uom={priceUom}
              pricingType={corporatePart?.pricingType}
              buttons={[<EditPricingTypeButton key="edit" corporatePart={corporatePart} />]}
              highlighted
            />
            <VendorPriceCard
              groupName={t('headerFloorPrice')}
              listPrice={floorPriceValue}
              uom={priceUom}
              highlighted
            />
          </Stack>
        )}
        {!isLoading && isSetUp && <AgGridTable />}
        {!isLoading && !isSetUp && <CorporatePartNotSetUp />}
      </SlidingPanelContent>
    </AgGridClientTableProvider>
  );
};
